import { Button, Download, Eye, EyeOff, Input, Trash2, Upload } from "@app/ui";
import { useState } from "react";
import { useBackupStore, useIcsStore, useSettingsStore } from "../../stores/index.js";
import { IcsExportDialog } from "../../features/ics/index.js";
import { useI18n } from "../../lib/useI18n.js";
import { exportSettingsFile, importSettingsFile } from "../../lib/settings-file.js";
import { SettingsCard } from "./SettingsCard.js";
import { SettingsSection } from "./SettingsSection.js";

export function DataSettings() {
  const { t } = useI18n();
  const anthropicApiKey = useSettingsStore((s) => s.anthropicApiKey);
  const setApiKey = useSettingsStore((s) => s.setApiKey);
  const icsBusy = useIcsStore((s) => s.busy);
  const importIcs = useIcsStore((s) => s.importIcs);
  const backupBusy = useBackupStore((s) => s.busy);
  const backupProgress = useBackupStore((s) => s.progress);
  const exportData = useBackupStore((s) => s.exportData);
  const importData = useBackupStore((s) => s.importData);
  const deleteAll = useBackupStore((s) => s.deleteAll);
  const [key, setKey] = useState("");
  const [reveal, setReveal] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const hasSavedKey = !!anthropicApiKey;

  return (
    <div className="flex flex-col gap-8">
      <SettingsSection title={t("data.integrations")}>
        <SettingsCard id="anthropic-key" title={t("data.apiKey")} description={t("data.apiKey.description")}>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              {/* Deliberately NOT type="password": a password input makes Bitwarden treat the
                  page as a login form and force-fill the user's email into the settings search
                  box (it ignores autocomplete/data-bwignore when pairing a "username" field).
                  Mask via CSS instead; the ignore attrs cover other password managers.
                  The saved key is never loaded back into the field — it stays out of the DOM. */}
              <Input
                type="text"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-bwignore="true"
                data-1p-ignore="true"
                data-lpignore="true"
                className={reveal ? undefined : "[-webkit-text-security:disc]"}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder={hasSavedKey ? t("data.apiKey.savedPlaceholder") : "sk-ant-..."}
              />
              {key && (
                <Button
                  variant="outline"
                  onClick={() => setReveal((v) => !v)}
                  aria-label={t(reveal ? "common.hide" : "common.show")}
                >
                  {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              )}
              <Button
                disabled={!key.trim()}
                onClick={() => {
                  void setApiKey(key.trim());
                  setKey("");
                  setReveal(false);
                }}
              >
                {t("common.save")}
              </Button>
            </div>
            {hasSavedKey && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{t("data.apiKey.saved")}</span>
                <Button variant="ghost" size="sm" onClick={() => void setApiKey("")}>
                  {t("common.remove")}
                </Button>
              </div>
            )}
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("data.importExport")}>
        <SettingsCard
          id="ics"
          title={t("data.ics")}
          description={t("data.ics.description")}
        >
          <div className="flex gap-2">
            <Button variant="outline" disabled={icsBusy} onClick={() => setExportOpen(true)}>
              <Download className="mr-2 h-4 w-4" /> {t("data.exportIcs")}
            </Button>
            <Button variant="outline" disabled={icsBusy} onClick={() => void importIcs()}>
              <Upload className="mr-2 h-4 w-4" /> {t("data.importIcs")}
            </Button>
          </div>
        </SettingsCard>

        <SettingsCard
          id="settings-file"
          title={t("data.settingsFile")}
          description={t("data.settingsFile.description")}
        >
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void exportSettingsFile()}>
              <Download className="mr-2 h-4 w-4" /> {t("data.exportSettings")}
            </Button>
            <Button variant="outline" onClick={() => void importSettingsFile()}>
              <Upload className="mr-2 h-4 w-4" /> {t("data.importSettings")}
            </Button>
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("data.backupRestore")}>
        <SettingsCard
          id="backup"
          title={t("data.backupRestore")}
          description={t("data.backupRestore.description")}
        >
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={backupBusy} onClick={() => void exportData()}>
              <Download className="mr-2 h-4 w-4" /> {t("data.saveToFile")}
            </Button>
            <Button variant="outline" disabled={backupBusy} onClick={() => void importData()}>
              <Upload className="mr-2 h-4 w-4" /> {t("data.loadFromFile")}
            </Button>
            <Button variant="destructive" disabled={backupBusy} onClick={() => void deleteAll()}>
              <Trash2 className="mr-2 h-4 w-4" /> {t("data.deleteAllData")}
            </Button>
          </div>
          {backupProgress && (
            <div className="mt-3 flex flex-col gap-1" role="status" aria-live="polite">
              <span className="text-xs text-muted-foreground">
                {t(backupProgress.phase === "clearing" ? "data.restore.clearing" : "data.restore.writing", {
                  percent: String(backupProgress.percent),
                })}
              </span>
              <div className="h-1 w-full overflow-hidden rounded bg-muted">
                <div className="h-full bg-primary transition-[width]" style={{ width: `${backupProgress.percent}%` }} />
              </div>
            </div>
          )}
        </SettingsCard>
      </SettingsSection>

      <IcsExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
    </div>
  );
}
